import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Visibility, VisibilityOff } from "@mui/icons-material";
import { IconButton, InputAdornment, Stack } from "@mui/material";

import { Button as GradientButton } from "@ui/index";

import { Formik } from "formik";
import { object, ref, string } from "yup";

import FormikInput from "@/components/FormikInput";
import PasswordRequirements from "@/components/PasswordRequirements";
import { ERecovery } from "@/layouts/SecondRootLayout";
import { IError } from "@/models/inner-models";
import { resetPassword, sendVerificationLink } from "@/store/actions/user";
import { useAppDispatchUnwrap } from "@/store/hooks";
import { LOGIN_NOTICE_KEY } from "@/utils/constants";
import { isStrongPassword } from "@/utils/passwordPolicy";

const initialValues = {
  email: "",
  password: "",
  passwordConfirmation: "",
};

const validationSchema = (t: (key: string) => string, step: ERecovery) => {
  switch (step) {
    case ERecovery.FirstStep:
      return object({
        email: string().email(t("emailValidation")).required(t("emailRequired")),
      });
    case ERecovery.ThirdStep:
      return object({
        // Full policy, not just length — mirrors the server rule.
        password: string()
          .required(t("passwordRequired"))
          .test("strength", t("passwordValidation"), (value) => isStrongPassword(value ?? "")),
        passwordConfirmation: string()
          .oneOf([ref("password"), ""], t("passwordsMatch"))
          .required(t("passwordConfirmationRequired")),
      });
  }
};

interface IForm {
  step: ERecovery;
  buttonText: string;
  handleNextStep: () => void;
  resetToken?: string | null;
}

const Form: FC<IForm> = ({ step, buttonText, handleNextStep, resetToken }) => {
  const { t } = useTranslation("login");
  const { t: tValidation } = useTranslation("validation");

  const [showPassword, setShowPassword] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };


  const dispatch = useAppDispatchUnwrap();

  // Top-level error string for the alert banner below the inputs. Captures
  // both backend rejection messages (from /send-verification-link-password
  // or /reset-password) and a friendlier override for the most common
  // backend error class-validator messages (e.g. "email must be an email").
  const [submitError, setSubmitError] = useState("");

  const friendlyBackendMessage = (msg: string): string => {
    if (!msg) return "";
    if (/email must be an email|invalid email|email.*format/i.test(msg)) return tValidation("emailValidation");
    if (/token.*expired|expired.*token/i.test(msg)) return "This password reset link has expired. Request a new one.";
    if (/incorrect token|invalid token|already been used|replaced/i.test(msg)) return "This password reset link is no longer valid. Request a new one.";
    if (/too many|rate limit/i.test(msg)) return "Too many attempts. Please wait a minute and try again.";
    return msg;
  };

  return (
    <Formik
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={() => validationSchema(tValidation, step)}
      onSubmit={async (values, { setFieldError }) => {
        setSubmitError("");
        if (step === ERecovery.FirstStep) {
          if (!values.email) return;
          try {
            await dispatch(sendVerificationLink({ email: values.email }));
            localStorage.setItem("email", values.email);
            handleNextStep();
          } catch (err) {
            const friendly = friendlyBackendMessage((err as IError).message);
            setFieldError("email", friendly);
            setSubmitError(friendly);
          }
        }

        if (step === ERecovery.ThirdStep) {
          try {
            if (!resetToken) return null;

            await dispatch(
              resetPassword({
                password: values.password,
                resetToken,
              }),
            );

            // Strip the token from the URL so a refresh / browser-back
            // doesn't reopen the reset form. Backend has also rotated
            // the JTI by this point (single-use), so even a stashed
            // copy of the link would be rejected on resubmit.
            searchParams.delete("token");
            setSearchParams(searchParams);

            // Stash the success notice for LoginDialog to surface on the
            // login panel. handleNextStep below swings the dialog back to
            // panel 0 (login), which reads + clears this key.
            try {
              sessionStorage.setItem(
                LOGIN_NOTICE_KEY,
                "Your password has been changed. Please log in again.",
              );
            } catch { /* sessionStorage blocked — noop */ }

            handleNextStep();
          } catch (err) {
            const friendly = friendlyBackendMessage((err as IError).message);
            setFieldError("password", friendly);
            setSubmitError(friendly);
          }
        }
      }}
    >
      {({ handleSubmit, isSubmitting, values, errors }) => (
        <form onSubmit={handleSubmit} noValidate style={{ width: "100%", zIndex: 5 }}>
          <Stack spacing="16px" alignItems="center" width="100%">
            <Stack spacing="16px" width="100%" alignItems="center" justifyContent="center">
              {step === ERecovery.FirstStep && (
                <FormikInput
                  fieldName="email"
                  label={t("email")}
                  placeholder={t("email") ?? ""}
                  type="text"
                  autoComplete="email"
                />
              )}

              {step === ERecovery.ThirdStep && (
                /* Match the authed change-password modal's spacing —
                   explicit gap via flex div so the literal pixel value
                   sticks (MUI Stack `spacing` with string values is
                   unreliable in this codebase, see ChangePasswordModal). */
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
                  <FormikInput
                    fieldName="password"
                    label={t("password")}
                    placeholder={t("password") ?? ""}
                    autoComplete="password"
                    hideErrorText
                    type={showPassword ? "text" : "password"}
                    InputAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOff sx={{ color: "textColor.highlighted" }} />
                          ) : (
                            <Visibility sx={{ color: "textColor.highlighted" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />

                  <PasswordRequirements password={(values as any).password ?? ""} />

                  <FormikInput
                    fieldName="passwordConfirmation"
                    label={t("passwordConfirmation")}
                    placeholder={t("passwordConfirmation") ?? ""}
                    autoComplete="password"
                    forceError={
                      ((values as any).passwordConfirmation?.length ?? 0) > 0 &&
                      (values as any).password !== (values as any).passwordConfirmation
                    }
                    type={showPassword ? "text" : "password"}
                    InputAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOff sx={{ color: "textColor.highlighted" }} />
                          ) : (
                            <Visibility sx={{ color: "textColor.highlighted" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />

                  {/* Live mismatch notice — same reason as the change-password
                      modal: validateOnChange/Blur are off, so yup only runs on
                      submit, and submit is blocked by the disabled button. The
                      user would otherwise click a mismatched pair and get
                      nothing. */}
                  {(values as any).passwordConfirmation?.length > 0 &&
                    (values as any).password !== (values as any).passwordConfirmation && (
                    <div
                      aria-live="polite"
                      style={{
                        color: "#e47a6a",
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                        fontSize: "0.75rem",
                        lineHeight: 1.5,
                        marginTop: -2,
                      }}
                    >
                      Passwords need to match
                    </div>
                  )}
                </div>
              )}
            </Stack>

            {/* Consolidated error alert. Surfaces (a) yup validation
                errors thrown on submit (e.g. "Please enter a valid email
                address."), and (b) backend rejection messages, both
                mapped to friendlier copy by `friendlyBackendMessage`.
                Lives in its own padded box so messages don't get squished
                into MUI's tiny FormHelperText slot below each input. */}
            {(() => {
              const e: any = errors;
              const message = submitError || e.email || e.password || e.passwordConfirmation || "";
              if (!message) return null;
              return (
                <div style={{
                  width: "100%",
                  padding: "14px 16px",
                  background: "rgba(255, 169, 68, 0.12)",
                  border: "1px solid #FFA944",
                  borderRadius: "8px",
                  fontFamily: "'Raleway', sans-serif",
                  fontSize: "0.9rem",
                  color: "#ffffff",
                  lineHeight: 1.5,
                }}>
                  {message}
                </div>
              );
            })()}

            {(() => {
              // Explicit value checks instead of Formik's `isValid` —
              // with `validateOnChange={false}` Formik defaults `isValid`
              // to true until the user submits, which would let the
              // button activate the moment any character is typed in
              // ThirdStep. Mirror the yup schema rules here.
              const v = values as any;
              const firstStepReady = !!(v.email && !isSubmitting);
              const thirdStepReady = isStrongPassword(v.password ?? "") && v.password === v.passwordConfirmation && !isSubmitting;
              const disabled = step === ERecovery.FirstStep ? !firstStepReady : !thirdStepReady;
              return (
                <GradientButton type="submit" isBigButton isFullWidth isDisabled={disabled}>
                  {buttonText}
                </GradientButton>
              );
            })()}
          </Stack>
        </form>
      )}
    </Formik>
  );
};

export default Form;
